import { Component, OnInit, TemplateRef, viewChild, inject } from '@angular/core';
import { Employee } from '../../data/employee';
import {
  BrowserMessagesService,
  ModalRef,
  ModalService,
  TableColumn,
  MultiSelectComponent, TableComponent, SideMenuHeadlineComponent
} from '@rbb/ng-web-template-lib';
import { EmployeeService } from '../../services/employee.service';
import { EmployeeOrgUnit } from '../../data/employee-org-unit';
import { BodyOrgUnitPath } from '../../body/body-org-unit-path';
import { OrgUnit } from '../../data/org-unit';
import { CostCentre } from '../../data/cost-centre';
import { Access } from '../../data/access';
import { AccessService } from '../../services/access.service';
import { AccessEmployee } from '../../data/access-employee';
import { ValidEmployeeOrgUnit } from '../../data/valid-employee-org-unit';
import { EmployeeType } from '../../types/employee-type';
import { OrgUnitApproved } from '../../types/org-unit-approved';
import { EmployeeOrgUnitService } from '../../services/employee-org-unit.service';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.css'],
  imports: [FormsModule, DatePipe, MultiSelectComponent, TableComponent, SideMenuHeadlineComponent]
})
export class EmployeeComponent implements OnInit {
  private modalService = inject(ModalService);
  private browserMessagesService = inject(BrowserMessagesService);
  private employeeOrgUnitService = inject(EmployeeOrgUnitService);
  private employeeService = inject(EmployeeService);
  private accessService = inject(AccessService);

  employee: Employee | undefined;
  orgUnits: OrgUnit[] | undefined;
  costCentres: CostCentre[] | undefined;

  employeeOrgUnits: EmployeeOrgUnit[] | undefined;
  validOrgUnits: ValidEmployeeOrgUnit[] | undefined;
  selectedValidOrgUnit: ValidEmployeeOrgUnit | undefined;
  selectedOrgUnit: OrgUnit[] = [];
  selectedCostCentre: CostCentre[] = [];
  orgUnitToDelete: EmployeeOrgUnit | undefined;

  changingActiveOrgUnit = false;

  toApprove = false;
  notifyToApprove = false;

  givenAccess: Access[] | undefined;
  receivedAccess: Access[] | undefined;
  accessEmployees: AccessEmployee[] | undefined;
  selectedEmployee: AccessEmployee[] | undefined;
  selectedAccess: Access | undefined;

  readonly changeOrgUnitModal = viewChild<TemplateRef<unknown>>('changeOrgUnitModal');
  changeOrgUnitModalService: ModalRef<unknown> | undefined;

  readonly orgUnitsHistoryModal = viewChild<TemplateRef<unknown>>('orgUnitsHistoryModal');
  orgUnitsHistoryModalService: ModalRef<unknown> | undefined;

  readonly createAccessModal = viewChild<TemplateRef<unknown>>('createAccessModal');
  createAccessModalService: ModalRef<unknown> | undefined;

  readonly revokeAccessModal = viewChild<TemplateRef<unknown>>('revokeAccessModal');
  revokeAccessModalService: ModalRef<unknown> | undefined;

  orgUnitsHistoryColumns: TableColumn[] = [
    { name: 'Org Einheit', field: 'orgUnitPath', type: 'string' },
    { name: 'Kostenstelle', field: 'costCentre', type: 'string' },
    { name: 'Status', field: 'approved', type: 'string' },
    { name: 'Anfrage gestellt am', field: 'timestamp', type: 'string' },
    { name: 'Geprüft am', field: 'approvedAt', type: 'string' },
    { name: '(Wird) Geprüft durch', field: 'approvedByFirstName', type: 'string' },
    { name: 'Ungültig ab', field: 'canceledAt', type: 'string' },
    { name: 'Ungültig gemacht durch', field: 'canceledByFirstName', type: 'string' },
    { name: '', field: 'delete', type: null }
  ];

  giveAccessColumns: TableColumn[] = [
    { name: 'Vorname', field: 'accessForFirstName', type: 'string' },
    { name: 'Nachname', field: 'accessForLastName', type: 'string' },
    { name: 'Benutzername', field: 'accessForUsername', type: 'string' },
    { name: 'Zu genehmigende Anträge', field: 'toApprove', type: 'boolean' },
    { name: 'Benachrichtigung', field: 'notifyToApprove', type: 'boolean', headerHint: 'Die Person wird benachrichtigt, wenn ein neuer zu genehmigender Antrag vorliegt.' },
    { name: 'Gewährt am', field: 'timestamp', type: 'string' },
    { name: '', field: 'delete', type: null }
  ];

  ngOnInit(): void {
    this.getMyData();
    this.getEmployeeOrgUnits();
    this.getOrgUnits();
    this.getCostCentres();
    this.getGivenAccess();
    this.getReceivedAccess();
    this.getAccessEmployees();
  }

  getMyData(): void {
    this.employeeService.getMyApprovers().subscribe({
      next: (next) => {
        this.employee = next;
        this.changingActiveOrgUnit = false;
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Mitarbeiterdaten konnten nicht geladen werden');
        this.changingActiveOrgUnit = false;
      }
    });
  }

  getValidOrgUnits(): void {
    this.employeeService.getValidOrgUnits().subscribe({
      next: (next) => {
        this.validOrgUnits = next;
        this.selectedValidOrgUnit = this.validOrgUnits.find(item => item.active);
        if (!this.selectedValidOrgUnit) {
          this.selectedValidOrgUnit = this.validOrgUnits.find(item => item.sap);
        }
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Gültige Organisationseinheiten konnten nicht geladen werden');
      }
    });
  }

  setOrgUnitAsActive(orgUnit: ValidEmployeeOrgUnit): void {
    this.changingActiveOrgUnit = true;
    this.employeeService.setOrgUnitAsActive(orgUnit.id).subscribe({
      next: () => {
        this.getMyData();
        this.getEmployeeOrgUnits();
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Die aktive Organisationseinheit konnte nicht verändert werden.');
        this.changingActiveOrgUnit = false;
      }
    });
  }

  getEmployeeOrgUnits(): void {
    this.employeeOrgUnitService.getOrgUnits().subscribe({
      next: (next) => {
        this.employeeOrgUnits = next;
        this.getValidOrgUnits();
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Organisationseinheiten konnten nicht geladen werden');
      }
    });
  }

  getOrgUnits(): void {
    this.employeeOrgUnitService.getAvailableOrgUnits().subscribe({
      next: (next) => {
        this.orgUnits = next;
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Mitarbeiterdaten konnten nicht geladen werden');
      }
    });
  }

  getCostCentres(): void {
    this.employeeOrgUnitService.getAvailableCostCentres().subscribe({
      next: (next) => {
        this.costCentres = next;
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Kostenstellen konnten nicht geladen werden');
      }
    });
  }

  getGivenAccess(): void {
    this.accessService.getGivenAccess().subscribe(next => this.givenAccess = next);
  }

  getReceivedAccess(): void {
    this.accessService.getReceivedAccess().subscribe(next => this.receivedAccess = next);
  }

  getAccessEmployees(): void {
    this.accessService.getEmployees().subscribe(next => this.accessEmployees = next);
  }

  sendOrgUnitRequest(): void {
    if (this.selectedOrgUnit?.[0]?.path && this.selectedCostCentre?.[0]?.costCentre) {
      this.employeeOrgUnitService.chooseOrgUnit(new BodyOrgUnitPath(this.selectedOrgUnit[0].path, this.selectedCostCentre[0].costCentre)).subscribe({
        next: () => {
          this.getEmployeeOrgUnits();
          this.selectedOrgUnit = [];
          this.selectedCostCentre = [];
          this.closeChangeOrgUnitModal();
          this.openOrgUnitsHistoryModal();
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Organisationseinheit konnte nicht gespeichert werden');
        }
      });
    }
  }

  deleteOrgUnit(): void {
    if (this.orgUnitToDelete?.id) {
      this.employeeOrgUnitService.deleteOrgUnit(this.orgUnitToDelete.id).subscribe({
        next: () => {
          this.orgUnitToDelete = undefined;
          this.getEmployeeOrgUnits();
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Prozess konnte nicht abgebrochen werden');
        }
      });
    }
  }

  createAccess(): void {
    if (this.selectedEmployee?.[0].username && this.selectedEmployee[0].uidNumber) {
      this.accessService.createAccess({
        accessForUsername: this.selectedEmployee[0].username,
        accessForUidNumber: this.selectedEmployee[0].uidNumber,
        toApprove: this.toApprove,
        notifyToApprove: this.toApprove && this.notifyToApprove
      }).subscribe({
        next: () => {
          this.getGivenAccess();
          this.closeCreateAccessModal();
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Zugriff konnte nicht gewährt werden');
        }
      });
    }
  }

  revokeAccess(): void {
    if (this.selectedAccess?.id) {
      this.accessService.revoke(this.selectedAccess.id).subscribe({
        next: () => {
          this.getGivenAccess();
          this.closeRevokeAccessModal();
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Zugriff konnte nicht gewährt werden');
        }
      });
    }
  }

  openChangeOrgUnitModal(): void {
    const changeOrgUnitModal = this.changeOrgUnitModal();
    if (changeOrgUnitModal) {
      this.changeOrgUnitModalService = this.modalService.open(changeOrgUnitModal, { centered: true, backdrop: 'static' });
    }
  }

  closeChangeOrgUnitModal(): void {
    this.changeOrgUnitModalService?.close();
  }

  openOrgUnitsHistoryModal(): void {
    this.orgUnitToDelete = undefined;
    const orgUnitsHistoryModal = this.orgUnitsHistoryModal();
    if (orgUnitsHistoryModal) {
      this.orgUnitsHistoryModalService = this.modalService.open(orgUnitsHistoryModal, { centered: true, size: 'xl' });
    }
  }

  closeOrgUnitsHistoryModal(): void {
    this.orgUnitsHistoryModalService?.close();
  }

  openCreateAccessModal(): void {
    this.selectedEmployee = [];
    this.toApprove = false;
    this.notifyToApprove = false;
    const createAccessModal = this.createAccessModal();
    if (createAccessModal) {
      this.createAccessModalService = this.modalService.open(createAccessModal, { centered: true, backdrop: 'static' });
    }
  }

  closeCreateAccessModal(): void {
    this.createAccessModalService?.close();
  }

  openRevokeAccessModal(access: Access): void {
    this.selectedAccess = access;
    const revokeAccessModal = this.revokeAccessModal();
    if (revokeAccessModal) {
      this.revokeAccessModalService = this.modalService.open(revokeAccessModal, { centered: true, backdrop: 'static' });
    }
  }

  closeRevokeAccessModal(): void {
    this.revokeAccessModalService?.close();
  }

  getOrgUnit(orgUnitPath: string): string {
    return orgUnitPath.split('/').reverse()[0];
  }

  hasInvalidOrgUnits(): boolean {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return !!this.employeeOrgUnits?.filter(employeeOrgUnit => employeeOrgUnit.approved === 'APPROVED' && (employeeOrgUnit.orgUnitInvalid || employeeOrgUnit.costCentreInvalid || employeeOrgUnit.costCentreIsOld)).length;
  }

  protected readonly EmployeeType = EmployeeType;
  protected readonly OrgUnitApproved = OrgUnitApproved;
}
